// ChannelReadService.js

// Firebase
import { collection, collectionGroup, doc, getDoc, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

const BASES_COLLECTION = collections.bases;
const sharedBaseChannelSubscriptions = new Map();
const sharedBaseReadReceiptSubscriptions = new Map();

/**
 * Messages held by the live channel listener. The window grows in steps when the
 * viewer asks for earlier messages, so a long-lived channel never streams its whole
 * history into the client.
 */
export const CHANNEL_MESSAGE_PAGE_SIZE = 75;

/** Replies held by the live thread listener; threads read oldest-first. */
export const THREAD_REPLY_PAGE_SIZE = 200;

const channelsDatabaseRef = (baseKey) => (
  collection(db, BASES_COLLECTION, baseKey, 'channels')
);

const channelDocRef = (baseKey, channelKey) => (
  doc(db, BASES_COLLECTION, baseKey, 'channels', channelKey)
);

const channelMessagesDatabaseRef = (baseKey, channelKey) => (
  collection(db, BASES_COLLECTION, baseKey, 'channels', channelKey, 'messages')
);

const channelReadDocRef = (baseKey, channelKey, userKey) => (
  doc(db, BASES_COLLECTION, baseKey, 'channels', channelKey, 'reads', userKey)
);

const getChannelSortValue = (channel) => (
  String(channel?.titleLower || channel?.title || '').trim().toLowerCase()
);

const mapMessageRow = (docSnap) => ({
  id: docSnap.id,
  ...(docSnap.data() || {}),
  key: docSnap.id,
});

/** Clamp a caller-supplied window size to something a client can hold. */
const clampWindowSize = (value, fallback) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return Math.min(Math.floor(parsed), 2000);
};

/**
 * Subscribes to base channels in real time.
 * Multiplexes one Firestore listener per base across Sidebar/Channel callers.
 *
 * @param {string} baseKey - Base key.
 * @param {function} callback - Callback receiving channel rows.
 * @param {function} onError - Optional callback receiving errors.
 * @param {object} options - Optional query options.
 * @param {string} options.viewerUserKey - Optional user key to scope channels by membership.
 *
 * @returns {function(): void} Unsubscribe function.
 */
export function subscribeToBaseChannels(baseKey, callback, onError, _options = {}) {
  const cleanBaseKey = String(baseKey || '').trim();
  if (!cleanBaseKey || typeof callback !== 'function') {
    return () => {};
  }

  let entry = sharedBaseChannelSubscriptions.get(cleanBaseKey);
  if (!entry) {
    entry = {
      key: cleanBaseKey,
      rows: [],
      listeners: new Set(),
      errorListeners: new Set(),
      unsubscribe: null,
    };

    const q = query(
      channelsDatabaseRef(cleanBaseKey),
      orderBy('titleLower', 'asc')
    );

    entry.unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const rows = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...(docSnap.data() || {}),
          key: docSnap.id,
        }));

        rows.sort((a, b) => getChannelSortValue(a).localeCompare(getChannelSortValue(b)));
        entry.rows = rows;
        entry.listeners.forEach((listener) => {
          try {
            listener(entry.rows);
          } catch (error) {
            console.error('[ChannelReadService] channel listener error:', error);
          }
        });
      },
      (error) => {
        entry.rows = [];
        entry.listeners.forEach((listener) => {
          try {
            listener([]);
          } catch (_error) {
            // ignore fan-out errors
          }
        });
        entry.errorListeners.forEach((listener) => {
          try {
            listener(error);
          } catch (_error) {
            // ignore fan-out errors
          }
        });
        if (entry.errorListeners.size === 0) {
          console.error('[ChannelReadService] subscribeToBaseChannels error:', error);
        }
      }
    );

    sharedBaseChannelSubscriptions.set(cleanBaseKey, entry);
  }

  entry.listeners.add(callback);
  if (typeof onError === 'function') {
    entry.errorListeners.add(onError);
  }
  callback(entry.rows);

  return () => {
    const current = sharedBaseChannelSubscriptions.get(cleanBaseKey);
    if (!current) return;

    current.listeners.delete(callback);
    if (typeof onError === 'function') {
      current.errorListeners.delete(onError);
    }
    if (current.listeners.size > 0) return;

    current.unsubscribe?.();
    sharedBaseChannelSubscriptions.delete(cleanBaseKey);
  };
}

/**
 * Subscribes to a single channel.
 *
 * @param {string} baseKey - Base key.
 * @param {string} channelKey - Channel key.
 * @param {function} callback - Callback receiving channel row.
 * @param {function} onError - Optional callback receiving errors.
 *
 * @returns {function(): void} Unsubscribe function.
 */
export function subscribeToChannel(baseKey, channelKey, callback, onError) {
  if (!baseKey || !channelKey || typeof callback !== 'function') {
    return () => {};
  }

  return onSnapshot(
    channelDocRef(baseKey, channelKey),
    (docSnap) => {
      const row = docSnap.exists()
        ? { id: docSnap.id, ...(docSnap.data() || {}), key: docSnap.id }
        : null;
      callback(row);
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      else console.error('[ChannelReadService] subscribeToChannel error:', error);
      callback(null);
    }
  );
}

/**
 * Subscribes to the most recent window of channel messages.
 *
 * The query runs newest-first with a limit and the rows are reversed for display, so
 * a channel with years of history costs one bounded window instead of every message.
 * Callers widen the window (see `windowSize`) to reveal earlier messages; keeping a
 * single growing listener means no gaps as new messages arrive and no stale rows when
 * messages are edited or deleted.
 *
 * @param {string} baseKey - Base key.
 * @param {string} channelKey - Channel key.
 * @param {function} callback - Receives `(rows, meta)`; rows ascending by date,
 *   `meta.hasMore` true when older messages may exist beyond the window.
 * @param {function} onError - Optional callback receiving errors.
 * @param {object} options - Optional query options.
 * @param {number} options.windowSize - Messages to hold (default `CHANNEL_MESSAGE_PAGE_SIZE`).
 *
 * @returns {function(): void} Unsubscribe function.
 */
export function subscribeToChannelMessages(baseKey, channelKey, callback, onError, options = {}) {
  if (!baseKey || !channelKey || typeof callback !== 'function') {
    return () => {};
  }

  const windowSize = clampWindowSize(options.windowSize, CHANNEL_MESSAGE_PAGE_SIZE);

  const q = query(
    channelMessagesDatabaseRef(baseKey, channelKey),
    orderBy('dateCreated', 'desc'),
    limit(windowSize)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs.map(mapMessageRow).reverse();
      callback(rows, {
        // A full window means there may be more behind it. If there is not, the next
        // widen simply returns the same rows and this settles to false.
        hasMore: snapshot.size >= windowSize,
        windowSize,
      });
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      else console.error('[ChannelReadService] subscribeToChannelMessages error:', error);
    }
  );
}

/**
 * Fetches a single channel message.
 *
 * @param {string} baseKey - Base key.
 * @param {string} channelKey - Channel key.
 * @param {string} messageKey - Message key.
 * @returns {Promise<object|null>} Message row or null.
 */
export async function getChannelMessage(baseKey, channelKey, messageKey) {
  const cleanMessageKey = String(messageKey || '').trim();
  if (!baseKey || !channelKey || !cleanMessageKey) return null;

  const docSnap = await getDoc(
    doc(db, BASES_COLLECTION, baseKey, 'channels', channelKey, 'messages', cleanMessageKey)
  );

  if (!docSnap.exists()) return null;

  return {
    id: docSnap.id,
    ...(docSnap.data() || {}),
    key: docSnap.id,
  };
}

/**
 * Subscribes to thread replies for a root message.
 *
 * Threads read oldest-first, so the window is an ascending limit that callers widen.
 * There is deliberately no "load every message and filter client-side" fallback: that
 * path turned a missing index into an unbounded read of the whole channel.
 *
 * @param {string} baseKey - Base key.
 * @param {string} channelKey - Channel key.
 * @param {string} threadRootKey - Thread root message key.
 * @param {function} callback - Receives `(rows, meta)`; `meta.hasMore` true when more
 *   replies may exist beyond the window.
 * @param {function} onError - Optional callback receiving errors.
 * @param {object} options - Optional query options.
 * @param {number} options.windowSize - Replies to hold (default `THREAD_REPLY_PAGE_SIZE`).
 * @returns {function(): void} Unsubscribe function.
 */
export function subscribeToThreadMessages(
  baseKey,
  channelKey,
  threadRootKey,
  callback,
  onError,
  options = {},
) {
  const cleanRootKey = String(threadRootKey || '').trim();
  if (!baseKey || !channelKey || !cleanRootKey || typeof callback !== 'function') {
    return () => {};
  }

  const windowSize = clampWindowSize(options.windowSize, THREAD_REPLY_PAGE_SIZE);

  const threadQuery = query(
    channelMessagesDatabaseRef(baseKey, channelKey),
    where('threadRootKey', '==', cleanRootKey),
    orderBy('dateCreated', 'asc'),
    limit(windowSize)
  );

  return onSnapshot(
    threadQuery,
    (snapshot) => {
      callback(snapshot.docs.map(mapMessageRow), {
        hasMore: snapshot.size >= windowSize,
        windowSize,
      });
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      else console.error('[ChannelReadService] subscribeToThreadMessages error:', error);
    }
  );
}

/**
 * Subscribes to every read receipt a user owns in one base, as a single listener.
 *
 * Receipts are stored per channel (`channels/{channelKey}/reads/{userKey}`), so the
 * unread rail used to open one listener per visible channel. This collection-group
 * query replaces that fan-out: the viewer's own receipt docs carry `userKey` and
 * `baseKey`, so all of them can be read at once.
 *
 * Multiplexes one Firestore listener per base+user across Sidebar/Channel.
 *
 * @param {string} baseKey - Base key.
 * @param {string} userKey - Viewer user key.
 * @param {function} callback - Callback receiving a `{ [channelKey]: receipt }` map.
 * @param {function} onError - Optional callback receiving errors.
 *
 * @returns {function(): void} Unsubscribe function.
 */
export function subscribeToBaseChannelReadReceipts(baseKey, userKey, callback, onError) {
  const cleanBaseKey = String(baseKey || '').trim();
  const cleanUserKey = String(userKey || '').trim();

  if (!cleanBaseKey || !cleanUserKey || typeof callback !== 'function') {
    return () => {};
  }

  const subscriptionKey = `${cleanBaseKey}::${cleanUserKey}`;
  let entry = sharedBaseReadReceiptSubscriptions.get(subscriptionKey);
  if (!entry) {
    entry = {
      key: subscriptionKey,
      receiptsByChannel: {},
      listeners: new Set(),
      errorListeners: new Set(),
      unsubscribe: null,
    };

    const q = query(
      collectionGroup(db, 'reads'),
      where('userKey', '==', cleanUserKey),
      where('baseKey', '==', cleanBaseKey)
    );

    entry.unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const receiptsByChannel = {};

        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const channelKey = String(data.channelKey || docSnap.ref.parent.parent?.id || '').trim();
          if (!channelKey) return;

          receiptsByChannel[channelKey] = { id: docSnap.id, ...data };
        });

        entry.receiptsByChannel = receiptsByChannel;
        entry.listeners.forEach((listener) => {
          try {
            listener(entry.receiptsByChannel);
          } catch (error) {
            console.error('[ChannelReadService] read-receipt listener error:', error);
          }
        });
      },
      (error) => {
        entry.receiptsByChannel = {};
        entry.listeners.forEach((listener) => {
          try {
            listener({});
          } catch (_error) {
            // ignore fan-out errors
          }
        });
        entry.errorListeners.forEach((listener) => {
          try {
            listener(error);
          } catch (_error) {
            // ignore fan-out errors
          }
        });
        if (entry.errorListeners.size === 0) {
          console.error('[ChannelReadService] subscribeToBaseChannelReadReceipts error:', error);
        }
      }
    );

    sharedBaseReadReceiptSubscriptions.set(subscriptionKey, entry);
  }

  entry.listeners.add(callback);
  if (typeof onError === 'function') {
    entry.errorListeners.add(onError);
  }
  callback(entry.receiptsByChannel);

  return () => {
    const current = sharedBaseReadReceiptSubscriptions.get(subscriptionKey);
    if (!current) return;

    current.listeners.delete(callback);
    if (typeof onError === 'function') {
      current.errorListeners.delete(onError);
    }
    if (current.listeners.size > 0) return;

    current.unsubscribe?.();
    sharedBaseReadReceiptSubscriptions.delete(subscriptionKey);
  };
}

/**
 * Subscribes to a user's read receipt for one channel.
 *
 * @param {string} baseKey - Base key.
 * @param {string} channelKey - Channel key.
 * @param {string} userKey - User key.
 * @param {function} callback - Callback receiving receipt or null.
 * @param {function} onError - Optional callback receiving errors.
 *
 * @returns {function(): void} Unsubscribe function.
 */
export function subscribeToChannelReadReceipt(baseKey, channelKey, userKey, callback, onError) {
  if (!baseKey || !channelKey || !userKey || typeof callback !== 'function') {
    return () => {};
  }

  return onSnapshot(
    channelReadDocRef(baseKey, channelKey, userKey),
    (docSnap) => {
      const row = docSnap.exists()
        ? { id: docSnap.id, ...(docSnap.data() || {}) }
        : null;
      callback(row);
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      else console.error('[ChannelReadService] subscribeToChannelReadReceipt error:', error);
      callback(null);
    }
  );
}
